/**
 * Shared video behavior for theme sections.
 * background — muted loop, autoplay when visible (JS-driven)
 * player     — custom play/mute controls, click-to-pause, progress bar
 */
window.ThemeVideo = {
  getBehavior(videoEl) {
    const wrapper = videoEl?.closest?.("[data-video-behavior]");
    return wrapper?.dataset?.videoBehavior || "background";
  },

  isBackground(videoEl) {
    return this.getBehavior(videoEl) === "background";
  },

  isPlayer(videoEl) {
    return this.getBehavior(videoEl) === "player";
  },

  playIfBackground(videoEl) {
    if (!videoEl || !this.isBackground(videoEl)) return;
    const playPromise = videoEl.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {});
    }
  },

  pause(videoEl) {
    if (videoEl) {
      videoEl.pause();
    }
  },

  updateSlideshowHeader() {
    const isPlaying = Boolean(
      document.querySelector(".theme-video--slideshow.theme-video--is-playing video")
    );
    document.documentElement.classList.toggle("slideshow-video-header-hidden", isPlaying);
  },

  initDynamicVideos(root = document) {
    const intersectionObs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const videoEl = entry.target;
        if (entry.isIntersecting) {
          window.ThemeVideo.playIfBackground(videoEl);
        } else {
          window.ThemeVideo.pause(videoEl);
        }
      });
    });

    const updateVideoSource = (videoEl, srcUrl) => {
      let source = videoEl.querySelector("source");
      if (source) {
        if (source.src !== srcUrl) {
          if (srcUrl) {
            source.src = srcUrl;
            videoEl.load();
            intersectionObs.unobserve(videoEl);
            intersectionObs.observe(videoEl);
          } else {
            source.remove();
            videoEl.load();
          }
        }
      } else if (srcUrl) {
        source = document.createElement("source");
        source.type = "video/mp4";
        source.src = srcUrl;
        videoEl.append(source);
      }
    };

    const resizeObs = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const url =
          document.body.clientWidth > 575
            ? entry.target.dataset.urlDesktop
            : entry.target.dataset.urlMobile;
        updateVideoSource(entry.target, url);
      });
    });

    root.querySelectorAll(".js-dynamic-video").forEach((video) => {
      if (video.dataset.themeVideoInit === "true") return;
      video.dataset.themeVideoInit = "true";
      resizeObs.observe(video);
      if (window.ThemeVideo.isBackground(video)) {
        intersectionObs.observe(video);
      }
    });
  },

  initPlayerControls(root = document) {
    root.querySelectorAll('[data-video-behavior="player"]').forEach((wrapper) => {
      if (wrapper.dataset.playerInit === "true") return;
      wrapper.dataset.playerInit = "true";

      const shell = wrapper.querySelector(".theme-video__shell");
      const video = wrapper.querySelector("video");
      const playBtn = wrapper.querySelector(".theme-video__button--play");
      const muteBtn = wrapper.querySelector(".theme-video__button--mute");
      const posterBtn = wrapper.querySelector(".theme-video__poster");
      const progressTrack = wrapper.querySelector(".theme-video__progress-track");
      const progressFill = wrapper.querySelector(".theme-video__progress-fill");
      if (!video || !playBtn || !muteBtn || !shell) return;

      const isSlideshow = wrapper.classList.contains("theme-video--slideshow");
      let idleTimer = null;

      const pauseOtherPlayers = () => {
        document.querySelectorAll('[data-video-behavior="player"] video').forEach((otherVideo) => {
          if (otherVideo !== video && !otherVideo.paused) {
            otherVideo.pause();
            const otherWrapper = otherVideo.closest('[data-video-behavior="player"]');
            otherWrapper?.classList.remove("theme-video--controls-hidden");
          }
        });
      };

      const updatePlayState = () => {
        wrapper.classList.toggle("theme-video--is-playing", !video.paused);
        playBtn.setAttribute("aria-label", video.paused ? "Play video" : "Pause video");

        if (video.paused) {
          wrapper.classList.remove("theme-video--controls-hidden");
          clearTimeout(idleTimer);
        } else {
          resetIdleTimer();
        }

        if (isSlideshow) {
          window.ThemeVideo.updateSlideshowHeader();
        }
      };

      const updateMuteState = () => {
        wrapper.classList.toggle("theme-video--is-muted", video.muted);
        muteBtn.setAttribute("aria-label", video.muted ? "Unmute video" : "Mute video");
      };

      const updateProgress = () => {
        if (!progressFill || !video.duration || Number.isNaN(video.duration)) return;
        const percent = (video.currentTime / video.duration) * 100;
        progressFill.style.width = `${Math.min(Math.max(percent, 0), 100)}%`;
      };

      const resetIdleTimer = () => {
        clearTimeout(idleTimer);
        wrapper.classList.remove("theme-video--controls-hidden");

        if (video.paused) return;

        idleTimer = setTimeout(() => {
          wrapper.classList.add("theme-video--controls-hidden");
        }, 2000);
      };

      const resetToStart = () => {
        video.pause();
        video.currentTime = 0;
        wrapper.classList.remove("theme-video--started");
        wrapper.classList.remove("theme-video--is-playing");
        wrapper.classList.remove("theme-video--controls-hidden");
        clearTimeout(idleTimer);
        updatePlayState();
        updateProgress();
      };

      const startPlayback = () => {
        pauseOtherPlayers();
        wrapper.classList.add("theme-video--started");
        video.play().catch(() => {});
      };

      const togglePlayPause = () => {
        if (video.paused) {
          startPlayback();
        } else {
          video.pause();
        }
      };

      playBtn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        togglePlayPause();
      });

      muteBtn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        video.muted = !video.muted;
        updateMuteState();
        resetIdleTimer();
      });

      posterBtn?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        startPlayback();
      });

      progressTrack?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!video.duration || Number.isNaN(video.duration)) return;

        const rect = progressTrack.getBoundingClientRect();
        const percent = (event.clientX - rect.left) / rect.width;
        video.currentTime = Math.min(Math.max(percent, 0), 1) * video.duration;
        updateProgress();
        resetIdleTimer();
      });

      shell.addEventListener("click", (event) => {
        if (event.target.closest(".theme-video__button, .theme-video__progress-track, .theme-video__poster")) {
          return;
        }

        if (isSlideshow && !wrapper.classList.contains("theme-video--started")) {
          startPlayback();
          return;
        }

        togglePlayPause();
      });

      wrapper.addEventListener("mousemove", resetIdleTimer);
      wrapper.addEventListener("touchstart", resetIdleTimer, { passive: true });

      video.addEventListener("play", updatePlayState);
      video.addEventListener("pause", updatePlayState);
      video.addEventListener("volumechange", updateMuteState);
      video.addEventListener("timeupdate", updateProgress);
      video.addEventListener("loadedmetadata", updateProgress);
      video.addEventListener("ended", resetToStart);

      if (!isSlideshow) {
        wrapper.classList.add("theme-video--started");
      }

      updatePlayState();
      updateMuteState();
      updateProgress();
    });
  },
};

document.addEventListener("DOMContentLoaded", () => {
  window.ThemeVideo.initDynamicVideos();
  window.ThemeVideo.initPlayerControls();
});

document.addEventListener("shopify:section:load", (event) => {
  window.ThemeVideo.initDynamicVideos(event.target);
  window.ThemeVideo.initPlayerControls(event.target);
});
